import React, { useState, useEffect } from 'react';
import store, { hmdaActions } from '@apply/adminStore';
import Dialog from '@material-ui/core/Dialog';
import { FieldType, FieldTypeOptions } from '../common/HmdaConstants';
import { NumericFormat } from 'react-number-format';
import DateBox from 'devextreme-react/date-box';
import { HmdaApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { useSelector } from 'react-redux';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const EditModal = () => {
    const { isOpen, fieldId, fields, data } = useSelector((state) => state.hmda.hmdaEditModal);
    const { dispatch } = store;
    const hmdaApi = new HmdaApi.HmdaApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [field, set_field] = useState(null);
    const [origFieldValue, set_origFieldValue] = useState('');
    const [dataValue, set_dataValue] = useState('');
    const [inputType, set_inputType] = useState('0');
    const [otherFieldsRequired, set_otherFieldsRequired] = useState([]);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const styles = {
        sectionHeader: { margin: '10px 0' },
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
        formRow: { marginBottom: '0' },
        leftLabel: { paddingTop: '10px', whiteSpace: 'nowrap' },
        infoSection: {
            backgroundColor: '#c0c0c07a',
            display: 'inline-block',
            width: '100%',
            padding: '5px',
            marginTop: '5px',
            marginBottom: '10px',
        },
        infoSectionTable: { marginBottom: '0', margin: '5px' },
        infoSectionLeft: {
            width: '120px',
            verticalAlign: 'top',
            padding: '3px',
        },
        infoSectionRight: { fontWeight: 'bold', padding: '3px' },
    };

    useEffect(() => {
        if (fields && fieldId) {
            const fld = fields.find((a) => a.id === fieldId);
            set_field(fld);
        }
    }, [fields, fieldId]);

    useEffect(() => {
        if (!field || !data) {
            return;
        }

        var option = field.options.find((a) => a.code === dataValue);
        let val = null;
        if (data) {
            val = data['field' + field.id.toString()];
            set_dataValue(val ?? '');
            set_origFieldValue(val);
        } else {
            set_dataValue('');
        }
        set_inputType(option ? '1' : '0');
    }, [field, data]);

    useEffect(() => {
        if (!dataValue || !fields) {
            return;
        }

        //See if other feilds are required based on the seected field
        const otherFields = [];
        fields.forEach((f) => {
            if (f.id !== fieldId && f.requiredByFieldId === fieldId && f.requiredByFieldValue === dataValue) {
                otherFields.push(`${f.id}: ${f.shortName}`);
            }
        });

        set_otherFieldsRequired(otherFields);
    }, [dataValue]);

    const handleClose = () => dispatch(hmdaActions.setHmdaEditModal({ hmdaEditModal: { isOpen: false } }));

    const saveChanges = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id: data.id,
                fieldId: fieldId,
                value: dataValue,
            };
            const response = await hmdaApi.setData(model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Field Saved successfully.');
            set_loadingOpen(false);

            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    if (!isOpen || !field) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='md'
                open={isOpen}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '700px', overflow: 'unset' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1 className='page-header-single-h1'>{field.shortName}</h1>
                            </div>
                        </div>
                        <div
                            className='main-content no-search'
                            style={{ padding: '0', width: '100%' }}
                        >
                            <form
                                name='addEditLoanForm'
                                id='addEditLoanForm'
                                onSubmit={(event) => saveChanges(event)}
                                className={'standard-form'}
                            >
                                <div style={{ padding: '0px 8px 0 8px' }}>
                                    <div style={styles.infoSection}>
                                        <div className='row'>
                                            <div
                                                id='fullName'
                                                className='columns small-12 large-12'
                                                style={styles.formColumn}
                                            >
                                                <table style={styles.infoSectionTable}>
                                                    <tr>
                                                        <td style={styles.infoSectionLeft}>Data Field #:</td>
                                                        <td style={styles.infoSectionRight}>{field.id}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={styles.infoSectionLeft}>Full Name:</td>
                                                        <td style={styles.infoSectionRight}>{field.fullName}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={styles.infoSectionLeft}>Type:</td>
                                                        <td style={styles.infoSectionRight}>
                                                            {FieldTypeOptions.find((f) => f.id === field.fieldTypeId).name}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={styles.infoSectionLeft}>BFI Relevant:</td>
                                                        <td style={styles.infoSectionRight}>
                                                            {field.bfiRelevant ? 'Yes' : 'No'}
                                                        </td>
                                                    </tr>
                                                    {field.fieldTypeId === FieldType.AlphaNumeric && (
                                                        <tr>
                                                            <td style={styles.infoSectionLeft}>Max Length</td>
                                                            <td style={styles.infoSectionRight}>{field.maxLength}</td>
                                                        </tr>
                                                    )}
                                                    <tr>
                                                        <td style={styles.infoSectionLeft}>Required:</td>
                                                        <td style={styles.infoSectionRight}>{field.isRequired ? 'Yes' : 'No'}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={styles.infoSectionLeft}>Data Source:</td>
                                                        <td
                                                            style={{
                                                                padding: '3px',
                                                            }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: field.sourceDescription,
                                                            }}
                                                        ></td>
                                                    </tr>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    {field.fieldTypeId !== FieldType.Lookup && field.options.length > 0 && (
                                        <div className='row'>
                                            <div className='columns small-12 large-12'>
                                                <input
                                                    name='inputType'
                                                    type='radio'
                                                    value='0'
                                                    checked={inputType === '0'}
                                                    onClick={(e) => set_inputType(e.target.value)}
                                                    disabled={!field.isEditable}
                                                />
                                                &nbsp; Manual Input&nbsp;&nbsp;&nbsp;&nbsp;
                                                <input
                                                    name='inputType'
                                                    type='radio'
                                                    value='1'
                                                    checked={inputType === '1'}
                                                    onClick={(e) => set_inputType(e.target.value)}
                                                    disabled={!field.isEditable}
                                                />
                                                &nbsp; Select Input
                                            </div>
                                        </div>
                                    )}
                                    <div className='row'>
                                        <div className='columns small-12 large-12'>
                                            <label htmlFor='fieldValue'>
                                                Value {field.isRequired && <span className='req'>*</span>}
                                            </label>
                                            {field.options.length > 0 &&
                                                (field.fieldTypeId === FieldType.Lookup || inputType === '1') && (
                                                    <select
                                                        id='term'
                                                        value={dataValue ?? ''}
                                                        onChange={(event) => set_dataValue(event.target.value)}
                                                        required={field.isRequired}
                                                        disabled={!field.isEditable}
                                                    >
                                                        {field.fieldTypeId === FieldType.Lookup && !field.isRequired && (
                                                            <option
                                                                key=''
                                                                value=''
                                                            >
                                                                *No Entry*
                                                            </option>
                                                        )}
                                                        {field.options
                                                            .sort((a) => a.code)
                                                            .map((poc) => {
                                                                return (
                                                                    <option
                                                                        key={poc.code}
                                                                        value={poc.code}
                                                                    >
                                                                        {poc.code} - {poc.value}
                                                                    </option>
                                                                );
                                                            })}
                                                    </select>
                                                )}
                                            {inputType === '0' && (
                                                <>
                                                    {field.fieldTypeId === FieldType.AlphaNumeric && (
                                                        <div
                                                            style={{
                                                                marginTop: '10px',
                                                            }}
                                                        >
                                                            <input
                                                                id='fieldValue'
                                                                type='text'
                                                                maxLength={field.maxLength}
                                                                required={field.isRequired}
                                                                value={dataValue}
                                                                disabled={!field.isEditable}
                                                                placeholder='Enter value'
                                                                onChange={(e) => set_dataValue(e.target.value)}
                                                            />
                                                        </div>
                                                    )}
                                                    {field.fieldTypeId === FieldType.ZipCode && (
                                                        <div
                                                            style={{
                                                                marginTop: '10px',
                                                            }}
                                                        >
                                                            <input
                                                                id='fieldValue'
                                                                type='text'
                                                                maxLength={field.maxLength}
                                                                required={field.isRequired}
                                                                value={dataValue}
                                                                disabled={!field.isEditable}
                                                                placeholder='Enter value'
                                                                onChange={(e) => set_dataValue(e.target.value)}
                                                            />
                                                        </div>
                                                    )}
                                                    {field.fieldTypeId === FieldType.Currency && (
                                                        <div
                                                            style={{
                                                                marginTop: '10px',
                                                                width: '150px',
                                                            }}
                                                        >
                                                            <NumericFormat
                                                                thousandSeparator=','
                                                                prefix='$'
                                                                id='fieldValue'
                                                                placeholder='$'
                                                                value={dataValue}
                                                                decimalScale={2}
                                                                allowNegative={false}
                                                                onValueChange={(e) => set_dataValue(e.value)}
                                                                required={field.isRequired}
                                                                isAllowed={(values, sourceInfo) => {
                                                                    const value = values?.floatValue;
                                                                    return value === undefined || value === null || value >= 0;
                                                                }}
                                                                disabled={!field.isEditable}
                                                            />
                                                        </div>
                                                    )}
                                                    {field.fieldTypeId === FieldType.Integer && (
                                                        <div
                                                            style={{
                                                                marginTop: '10px',
                                                                width: '150px',
                                                            }}
                                                        >
                                                            <NumericFormat
                                                                thousandSeparator=','
                                                                id='fieldValue'
                                                                value={dataValue}
                                                                decimalScale={0}
                                                                allowNegative={true}
                                                                onValueChange={(e) => set_dataValue(e.value)}
                                                                required={field.isRequired}
                                                                disabled={!field.isEditable}
                                                            />
                                                        </div>
                                                    )}
                                                    {field.fieldTypeId === FieldType.Decimal && (
                                                        <div
                                                            style={{
                                                                marginTop: '10px',
                                                                width: '150px',
                                                            }}
                                                        >
                                                            <NumericFormat
                                                                thousandSeparator=','
                                                                id='fieldValue'
                                                                value={dataValue}
                                                                decimalScale={3}
                                                                allowNegative={true}
                                                                onValueChange={(e) => set_dataValue(e.value)}
                                                                required={field.isRequired}
                                                                disabled={!field.isEditable}
                                                            />
                                                        </div>
                                                    )}
                                                    {field.fieldTypeId === FieldType.DecimalSinglePrecision && (
                                                        <div
                                                            style={{
                                                                marginTop: '10px',
                                                                width: '150px',
                                                            }}
                                                        >
                                                            <NumericFormat
                                                                thousandSeparator=','
                                                                id='fieldValue'
                                                                value={dataValue}
                                                                decimalScale={1}
                                                                allowNegative={true}
                                                                onValueChange={(e) => set_dataValue(e.value)}
                                                                required={field.isRequired}
                                                                disabled={!field.isEditable}
                                                            />
                                                        </div>
                                                    )}
                                                    {field.fieldTypeId === FieldType.Date && (
                                                        <div
                                                            style={{
                                                                marginTop: '10px',
                                                                width: '150px',
                                                            }}
                                                        >
                                                            <DateBox
                                                                applyValueMode='useButtons'
                                                                id='fieldValue'
                                                                value={dataValue ? new Date(dataValue) : null}
                                                                onValueChange={(e) => set_dataValue(e)}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {otherFieldsRequired && otherFieldsRequired.length > 0 && (
                                        <div className='row'>
                                            <div
                                                className='columns small-12 large-12'
                                                style={{ color: 'darkred' }}
                                            >
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                &nbsp;The following {otherFieldsRequired === 1 ? 'field is' : 'field(s) are'} now
                                                required:
                                                <div
                                                    style={{
                                                        marginLeft: '30px',
                                                    }}
                                                >
                                                    <ul>
                                                        {otherFieldsRequired.map((f, index) => {
                                                            return <li key={'of_' + index}>{f}</li>;
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='submit'
                                        className='button primary'
                                        title='Save all changes'
                                        disabled={origFieldValue === dataValue}
                                    >
                                        Save
                                    </button>
                                    <button
                                        type='button'
                                        className='button hollow'
                                        data-dismiss='modal'
                                        onClick={handleClose}
                                        title='Abandon changes and close this window'
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default EditModal;
